import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Zap, DollarSign, Palette, ArrowRight, Star } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AnimateOnScroll from "@/components/AnimateOnScroll";
import { usePageSeo } from "@/hooks/use-page-seo";

const benefits = [
{
  icon: Zap,
  title: "Lightning Fast",
  description: "AI-assisted development means your website is built faster than ever — often in days, not weeks."
},
{
  icon: DollarSign,
  title: "Affordable Pricing",
  description: "Professional websites starting at just $499 upfront, or $0 down with our monthly plan."
},
{
  icon: Palette,
  title: "Modern Design",
  description: "Beautiful, responsive websites that look great on every device and reflect your brand perfectly."
}];


const Index = () => {
  usePageSeo("home");
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Hero */}
      <section id="main-content" className="relative bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-secondary/30" />
        <div className="container mx-auto px-4 py-20 md:py-28 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bg-background/90 rounded-2xl p-6 inline-block mb-8">
              <picture>
                <source srcSet="/LKNTechLogo.webp" type="image/webp" />
                <img
                  src="/LKNTechLogo.png"
                  alt="Lake Norman Technologies — AI-assisted web development in Mooresville, NC"
                  className="h-64 md:h-80 w-auto mx-auto aspect-[580/374]"
                  loading="eager"
                  fetchPriority="high"
                  decoding="sync" />
              </picture>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              AI-Assisted Web &amp; App Development
            </h1>
            <AnimateOnScroll delay={300}>
              <p className="text-lg md:text-xl text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
                Professional, affordable websites for small businesses — powered by the latest AI technology. Based in beautiful Mooresville, NC on Lake Norman.
              </p>
            </AnimateOnScroll>
            <AnimateOnScroll delay={450}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-base font-semibold px-8">
                  <Link to="/pricing">View Pricing</Link>
                </Button>
                <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 text-base font-semibold px-8">
                  <Link to="/contact">Get in Touch</Link>
                </Button>
              </div>
            </AnimateOnScroll>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 40C360 80 720 0 1080 40C1260 60 1380 50 1440 40V80H0V40Z" fill="hsl(40, 33%, 97%)" />
          </svg>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <AnimateOnScroll>
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Why Choose Us?</h2>
            <p className="text-center text-muted-foreground mb-12 max-w-xl mx-auto">
              We combine cutting-edge AI tools with human expertise to deliver stunning websites at unbeatable prices.
            </p>
          </AnimateOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((b, i) =>
            <AnimateOnScroll key={b.title} delay={i * 150}>
                <div className="bg-card rounded-lg p-8 text-center shadow-sm border border-border hover:shadow-md transition-shadow h-full">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-accent/15 text-accent mb-5">
                    <b.icon className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{b.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{b.description}</p>
                </div>
              </AnimateOnScroll>
            )}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="bg-muted py-16">
        <AnimateOnScroll className="container mx-auto px-4 text-center max-w-2xl">
          <div className="flex justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) =>
            <Star key={i} className="h-5 w-5 fill-accent text-accent" />
            )}
          </div>
          <blockquote className="text-lg md:text-xl italic text-foreground/80 mb-4">"Lake Norman Technologies built our website in record time and at a price I could actually afford. The quality exceeded our expectations!"

          </blockquote>
          <p className="font-semibold text-foreground">— Alana Michaels</p>
        </AnimateOnScroll>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20">
        <AnimateOnScroll className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
            Get a professional website for your business starting at just $499 — or $0 down with our monthly plan.
          </p>
          <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-base font-semibold px-8">
            <Link to="/pricing">
              See Our Plans <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </AnimateOnScroll>
      </section>

      <Footer />
    </div>);

};

export default Index;